/**
 * TransferReportsConfirmStep View Component
 *
 * Confirmation screen requiring the user to type "transfer" before
 * proceeding. Shows a warning with player names and item count,
 * matching the platform's existing confirmation dialog aesthetic.
 * @module features/player-detail/components/TransferReportsConfirmStep
 */

import { memo } from "react";

import { Button } from "@/components/atoms/Button";
import { Input } from "@/components/atoms/Input";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";

/** Props for TransferReportsConfirmStepView */
export interface TransferReportsConfirmStepViewProps {
  /** Source manual player name */
  readonly sourcePlayerName: string;
  /** Target player display name */
  readonly targetPlayerName: string;
  /** Total number of items to transfer */
  readonly totalItemCount: number;
  /** Whether the confirm button should be enabled */
  readonly isConfirmEnabled: boolean;
  /**
   * Callback when confirmation input text changes.
   *
   * This is the ONLY path the typed text takes out of the field. It feeds the
   * Container's gate comparison and nothing else — see the note on the input
   * below for why nothing may feed it back in.
   */
  readonly onChangeText: (text: string) => void;
  /** Callback fired when user confirms the transfer */
  readonly onConfirm: () => void;
  /** Callback fired when user goes back */
  readonly onBack: () => void;
}

/**
 * Confirmation dialog with "type transfer" safety input
 * @param root0 - Component props
 * @param root0.sourcePlayerName - Source manual player name
 * @param root0.targetPlayerName - Target player display name
 * @param root0.totalItemCount - Total items to transfer
 * @param root0.isConfirmEnabled - Whether confirm button is enabled
 * @param root0.onChangeText - Text change handler
 * @param root0.onConfirm - Confirm handler
 * @param root0.onBack - Back handler
 */
const TransferReportsConfirmStepView = ({
  sourcePlayerName,
  targetPlayerName,
  totalItemCount,
  isConfirmEnabled,
  onChangeText,
  onConfirm,
  onBack,
}: TransferReportsConfirmStepViewProps) => (
  <Stack className="gap-5 p-5">
    {/* Warning text */}
    <Stack className="gap-2">
      <Text variant="title-sm" className="text-content-primary">
        Are you sure?
      </Text>
      <Text variant="body" className="leading-5 text-content-secondary">
        This will permanently transfer{" "}
        <Text variant="title-sm" className="text-content-primary">
          {totalItemCount} {totalItemCount === 1 ? "item" : "items"}
        </Text>{" "}
        from{" "}
        <Text variant="title-sm" className="text-content-primary">
          {sourcePlayerName}
        </Text>{" "}
        to{" "}
        <Text variant="title-sm" className="text-content-primary">
          {targetPlayerName}
        </Text>
        . This action cannot be undone.
      </Text>
    </Stack>

    {/* Confirmation input */}
    <Stack className="gap-2">
      <Text variant="caption" className="text-content-secondary">
        Type{" "}
        <Text variant="title-sm" className="text-status-warning">
          transfer
        </Text>{" "}
        to confirm
      </Text>
      {/*
        SE-7733: the field is UNCONTROLLED on purpose. `defaultValue` is read
        once when the field mounts and never written again, so the typed text
        only ever flows OUT through `onChangeText` — there is no JS→native
        value write that can land late and overwrite in-flight keystrokes.
        Dropping a character requires such a write carrying stale text; with a
        single mount-time write there is no queue for one to arrive from, so
        the mechanism is removed rather than narrowed.

        Do NOT reintroduce a `value` prop here (that is exactly the defect),
        and do not pass the Container's state as the default either — that
        reads as controlled and invites it straight back in. Emptiness on
        re-entry comes from the unmount/remount boundary in
        TransferReportsModalView, which renders this step behind
        `showConfirm && targetPlayer ? … : null`.
      */}
      <Input
        size="md"
        field={{
          placeholder: "transfer",
          defaultValue: "",
          onChangeText,
          autoCapitalize: "none",
          autoCorrect: false,
          testID: "transfer-confirm-input",
        }}
      />
    </Stack>

    {/* Action Buttons */}
    <Stack direction="horizontal" className="justify-end gap-3 pt-2">
      <Button
        label="Back"
        variant="outline"
        tone="secondary"
        size="sm"
        labelTone="secondary"
        onPress={onBack}
        testID="transfer-confirm-back"
      />

      <Button
        label="Confirm Transfer"
        tone="primary"
        size="sm"
        labelTone="on-accent"
        onPress={onConfirm}
        isDisabled={!isConfirmEnabled}
        testID="transfer-confirm-button"
      />
    </Stack>
  </Stack>
);

TransferReportsConfirmStepView.displayName = "TransferReportsConfirmStepView";

export default memo(TransferReportsConfirmStepView);
